import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { emailApi, authApi, paymentsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Mail, 
  CheckCircle, 
  ArrowRight,
  Shield,
  Eye,
  Loader2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Onboarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isConnectingOutlook, setIsConnectingOutlook] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);

  // Check for payment success
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      checkPaymentAndRefreshUser(sessionId);
    }
    
    // Track StartTrial when user lands on onboarding
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'StartTrial', {
        content_name: 'SubGuard Onboarding',
        content_category: 'onboarding'
      });
    }
  }, [searchParams]);

  const checkPaymentAndRefreshUser = async (sessionId) => {
    try {
      const response = await paymentsApi.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Let\'s set up your account.');
        
        // Track Purchase event for Meta Pixel
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            content_name: response.data.plan || 'SubGuard Plan',
            content_category: 'subscription',
            value: response.data.amount || 0,
            currency: 'USD'
          });
        }
        
        // Refresh user data
        const userResponse = await authApi.getMe();
        updateUser(userResponse.data);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleConnectGmail = async () => {
    // Start Gmail OAuth flow
    setIsConnectingGmail(true);
    try {
      const redirectUri = `${BACKEND_URL}/api/auth/callback/gmail`;
      const response = await emailApi.startGmailOAuth(redirectUri);
      
      if (response.data.auth_url) {
        // Store onboarding state before redirecting
        localStorage.setItem('subguard_onboarding_step', '1');
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect Gmail';
      toast.error(message);
      setIsConnectingGmail(false);
    }
  };

  const handleConnectOutlook = async () => {
    setIsConnectingOutlook(true);
    try {
      const redirectUri = `${BACKEND_URL}/api/auth/callback/outlook`;
      const response = await emailApi.startOutlookOAuth(redirectUri);
      
      if (response.data.auth_url) {
        // Store onboarding state before redirecting
        localStorage.setItem('subguard_onboarding_step', '1');
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect Outlook';
      toast.error(message);
      setIsConnectingOutlook(false);
    }
  };

  const handleSkipEmail = () => {
    setStep(2);
  };

  const handleComplete = async () => {
    // Mark onboarding as complete
    try {
      await authApi.updateProfile({ onboarding_completed: true });
      // Refresh user data to ensure state is updated
      const userResponse = await authApi.getMe();
      updateUser(userResponse.data);
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
            alt="SubGuard" 
            className="w-12 h-12 rounded-xl"
          />
          <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            SubGuard
          </span>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                s === step ? 'w-8 bg-[#064E3B]' : s < step ? 'bg-[#064E3B]' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Connect Email */}
        {step === 1 && (
          <Card className="border-0 shadow-xl animate-fade-in">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#D1FAE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#064E3B]" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {t.onboarding.step1Title}
                </h1>
                <p className="text-gray-500">
                  {t.onboarding.step1Subtitle}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <Shield className="w-5 h-5 text-[#064E3B] mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Read-only access</p>
                    <p className="text-gray-500 text-xs">We only scan for subscription receipts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <Eye className="w-5 h-5 text-[#064E3B] mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Nothing stored</p>
                    <p className="text-gray-500 text-xs">Your email content is never saved</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Outlook - Available Now */}
                <Button
                  onClick={handleConnectOutlook}
                  disabled={isConnectingOutlook}
                  className="w-full h-14 bg-[#0078D4] hover:bg-[#106ebe] text-white rounded-xl"
                  data-testid="onboarding-connect-outlook"
                >
                  {isConnectingOutlook ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Connect Outlook / Microsoft
                    </>
                  )}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <button
                  onClick={handleSkipEmail}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                  data-testid="onboarding-skip-email"
                >
                  Continue manually (connect to email later)
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Complete */}
        {step === 2 && (
          <Card className="border-0 shadow-xl animate-fade-in">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#D1FAE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-[#064E3B]" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {t.onboarding.step3Title}
                </h1>
                <p className="text-gray-500">
                  {t.onboarding.step3Subtitle}
                </p>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 p-4 bg-[#D1FAE5] rounded-xl">
                  <CheckCircle className="w-5 h-5 text-[#064E3B]" />
                  <span className="text-[#064E3B] font-medium">Account created</span>
                </div>
                {outlookConnected && (
                  <div className="flex items-center gap-3 p-4 bg-blue-100 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-700 font-medium">Outlook connected</span>
                  </div>
                )}
                {gmailConnected && (
                  <div className="flex items-center gap-3 p-4 bg-[#D1FAE5] rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#064E3B]" />
                    <span className="text-[#064E3B] font-medium">Gmail connected</span>
                  </div>
                )}
                <div className="flex items-center gap-3 p-4 bg-[#D1FAE5] rounded-xl">
                  <CheckCircle className="w-5 h-5 text-[#064E3B]" />
                  <span className="text-[#064E3B] font-medium">Preferences saved</span>
                </div>
              </div>

              <Button
                onClick={handleComplete}
                className="w-full h-12 bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                data-testid="onboarding-complete"
              >
                {t.onboarding.goToDashboard}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
